import { apiClient } from './api';

export interface Salon {
  id: string;
  name: string;
  description?: string;
  slug?: string;
  phone?: string;
  email?: string;
  address: string;
  city: string;
  postalCode: string;
  logoUrl?: string;
  coverUrl?: string;
  publicTitle?: string;
  publicDescription?: string;
  publicPhone?: string;
  publicEmail?: string;
  publicAddress?: string;
  allowOnlineBooking?: boolean;
  allowPhoneContact?: boolean;
  isPublicPublished?: boolean;
  paymentsEnabled?: boolean;
  /** Wlaczone metody platnosci. Brak = wszystkie (salony sprzed tej zmiany). */
  enabledPaymentMethods?: string[] | null;
  isActive: boolean;
  ownerId: string;
  owner?: { id: string; email: string };
  services?: SalonService[];
  staff?: StaffMember[] | PublicStaffMember[];
  workingHours?: WorkingHours[];
  brand?: SalonBrandInfo | null;
  branches?: PublicBranch[];
  _count?: { services: number; staff: number; reviews: number };
}

/** Wszystkie metody, jakie zna system. Kolejnosc jest kolejnoscia na ekranach. */
export const ALL_PAYMENT_METHODS = [
  { value: 'CASH', label: 'Gotówka' },
  { value: 'CARD', label: 'Karta' },
  { value: 'BLIK', label: 'BLIK' },
  { value: 'TRANSFER', label: 'Przelew' },
  { value: 'BARTER', label: 'Barter' },
  { value: 'VOUCHER', label: 'Bon' },
  { value: 'DEPOSIT', label: 'Depozyt' },
] as const;

/**
 * Metody wlaczone w salonie. Pusta lub brakujaca konfiguracja znaczy
 * "wszystkie" — inaczej salon sprzed tej zmiany nie mialby czym zaplacic.
 */
export const enabledPaymentMethods = (
  salon?: { enabledPaymentMethods?: string[] | null } | null,
): string[] => {
  const enabled = salon?.enabledPaymentMethods;
  if (!enabled || enabled.length === 0) return ALL_PAYMENT_METHODS.map((m) => m.value as string);
  return enabled;
};

export interface SalonService {
  id: string;
  name: string;
  description?: string;
  publicDescription?: string;
  price: number;
  duration: number;
  category?: string;
  isActive: boolean;
  isPublicVisible?: boolean;
}

export interface PublicStaffMember {
  id: string;
  firstName: string;
  lastName: string;
  bio?: string;
  specialization?: string;
  services?: { service: { id: string; name: string } }[];
}

export interface PublicBranch {
  id: string;
  name: string;
  slug?: string;
  city: string;
  address: string;
  publicTitle: string;
  publicAddress: string;
  publicPhone?: string;
  allowOnlineBooking?: boolean;
  isCurrent?: boolean;
}

export interface SalonBrandInfo {
  id: string;
  name: string;
  description?: string;
  logoUrl?: string;
}

export interface StaffMember {
  id: string;
  firstName: string;
  lastName: string;
  phone?: string;
  bio?: string;
  isActive: boolean;
  user?: { email: string };
}

export interface WorkingHours {
  id: string;
  dayOfWeek: number;
  openTime: string;
  closeTime: string;
  isClosed: boolean;
}

export interface ScheduleException {
  id: string;
  date: string; // YYYY-MM-DD
  isClosed: boolean;
  openTime?: string | null;
  closeTime?: string | null;
  reason?: string | null;
}

interface PaginatedResponse<T> {
  data: T[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

interface CreateSalonData {
  name: string;
  description?: string;
  phone?: string;
  email?: string;
  address: string;
  city: string;
  postalCode: string;
  logoUrl?: string;
  coverUrl?: string;
  /** Tylko przy edycji — wlaczone metody platnosci. */
  enabledPaymentMethods?: string[];
}

export interface UpdatePublicProfileData {
  slug?: string;
  publicTitle?: string;
  publicDescription?: string;
  publicPhone?: string;
  publicEmail?: string;
  publicAddress?: string;
  allowOnlineBooking?: boolean;
  allowPhoneContact?: boolean;
}

export const salonApi = {
  async list(params?: { page?: number; search?: string; city?: string }) {
    const res = await apiClient.get<PaginatedResponse<Salon>>('/salons', { params });
    return res.data;
  },

  async getById(id: string) {
    const res = await apiClient.get<Salon>(`/salons/${id}`);
    return res.data;
  },

  async create(data: CreateSalonData) {
    const res = await apiClient.post<Salon>('/salons', data);
    return res.data;
  },

  async update(id: string, data: Partial<CreateSalonData>) {
    const res = await apiClient.put<Salon>(`/salons/${id}`, data);
    return res.data;
  },

  async delete(id: string) {
    const res = await apiClient.delete(`/salons/${id}`);
    return res.data;
  },

  async getWorkingHours(id: string) {
    const res = await apiClient.get<WorkingHours[]>(`/salons/${id}/working-hours`);
    return res.data;
  },

  async setWorkingHours(id: string, hours: Omit<WorkingHours, 'id'>[]) {
    const res = await apiClient.put(`/salons/${id}/working-hours`, { hours });
    return res.data;
  },

  async getScheduleExceptions(id: string) {
    const res = await apiClient.get<ScheduleException[]>(`/salons/${id}/schedule-exceptions`);
    return res.data;
  },

  async upsertScheduleException(id: string, data: Omit<ScheduleException, 'id'>) {
    const res = await apiClient.put(`/salons/${id}/schedule-exceptions`, data);
    return res.data;
  },

  async deleteScheduleException(id: string, exceptionId: string) {
    const res = await apiClient.delete(`/salons/${id}/schedule-exceptions/${exceptionId}`);
    return res.data;
  },

  async getPublicBySlug(slug: string) {
    const res = await apiClient.get<Salon>(`/salons/public/${slug}`);
    return res.data;
  },

  async updatePublicProfile(id: string, data: UpdatePublicProfileData) {
    const res = await apiClient.put<Salon>(`/salons/${id}/public-profile`, data);
    return res.data;
  },

  async setPublicationStatus(id: string, isPublicPublished: boolean) {
    const res = await apiClient.put<Salon>(`/salons/${id}/publication-status`, { isPublicPublished });
    return res.data;
  },
};
